import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Save, Sparkles, Target, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';

const FIELDS = [
  { key: 'objetivo_negocio', label: 'Objetivo de negocio del cliente', hint: 'Qué quiere conseguir la empresa (facturar más, abrir local, vender un servicio, captar cierto cliente). Objetivos de NEGOCIO, no de redes.' },
  { key: 'momento_negocio', label: 'Momento del negocio', hint: 'En qué punto está: arranca, quiere crecer, fidelizar, temporada alta/baja…' },
  { key: 'producto_priorizar', label: 'Producto / servicio a priorizar', hint: 'En qué se centra el foco ahora. Marca qué se empuja en orgánico y en ads.' },
  { key: 'propuesta_valor', label: 'Propuesta de valor y posicionamiento', hint: 'Qué le hace diferente y cómo queremos que se le perciba.' },
  { key: 'publico_prioritario', label: 'Público objetivo prioritario', hint: 'A quién interesa llegar sobre todo.' },
  { key: 'redes_ayudan', label: 'Cómo ayudan las redes al objetivo de negocio', hint: 'El puente entre el objetivo de negocio y lo que hacemos en orgánico y ads.' },
  { key: 'hitos_fechas', label: 'Hitos y fechas clave del negocio', hint: 'Lanzamientos, campañas, temporadas fuertes.' },
  { key: 'notas', label: 'Notas de estrategia', hint: 'Campo libre.' },
];

export default function BusinessStrategySection({ clientId }) {
  const [form, setForm] = useState(null);
  const [autoFields, setAutoFields] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialAutoRef = useRef(new Set());
  const editedRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getBusinessStrategy(clientId);
      const auto = Array.isArray(res?.auto_fields) ? res.auto_fields : [];
      setForm(res?.data || {});
      setAutoFields(auto);
      setCanEdit(!!res?.can_edit);
      initialAutoRef.current = new Set(auto);
      editedRef.current = new Set();
    } catch (e) {
      toast.error('Error cargando la estrategia de negocio');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setVal = (key, v) => {
    setForm((prev) => ({ ...prev, [key]: v }));
    editedRef.current.add(key);
    setAutoFields((prev) => prev.filter((f) => f !== key));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((f) => {
        const isPersistable = editedRef.current.has(f) || !initialAutoRef.current.has(f);
        payload[f] = isPersistable ? (form[f] || '') : '';
      });
      await serviceDeliveryAPI.saveBusinessStrategy(clientId, payload);
      toast.success('Estrategia de negocio guardada');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const RO = !canEdit;
  const AutoBadge = ({ field }) => autoFields.includes(field) ? (
    <Badge variant="outline" className="ml-2 bg-violet-50 text-violet-700 border-violet-200 text-[10px]" data-testid={`strategy-auto-${field}`}>
      <Sparkles className="w-2.5 h-2.5 mr-1" /> Del briefing
    </Badge>
  ) : null;

  return (
    <Card data-testid="business-strategy-section">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-primary" /> Estrategia de Negocio</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Visión global del cliente como empresa. Es el nivel superior del que dependen el orgánico y los ads.</p>
          </div>
          {RO ? (
            <Badge variant="secondary" className="gap-1"><Lock className="w-3 h-3" /> Solo lectura</Badge>
          ) : (
            <Button onClick={handleSave} disabled={saving} data-testid="business-strategy-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="flex items-center">{f.label}<AutoBadge field={f.key} /></Label>
            {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
            <Textarea
              value={form[f.key] || ''}
              onChange={(e) => setVal(f.key, e.target.value)}
              rows={3}
              disabled={RO}
              className="resize-none"
              data-testid={`strategy-${f.key}`}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
